import { effect, Signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { take } from 'rxjs';

export interface ServerConflictError {
  field: string;
  message: string;
}

/**
 * Wires a server-side conflict error (e.g. "name already exists") onto the matching form
 * control as a `serverDuplicate` validation error, clearing it automatically the next time
 * the user edits that field. Must be called from a component constructor (injection context).
 */
export function applyServerConflictError(
  form: FormGroup,
  conflictError: Signal<ServerConflictError | null | undefined>,
  destroyRef: DestroyRef,
  onConflict?: () => void,
): void {
  effect(() => {
    const conflict = conflictError();
    if (!conflict?.field) return;

    const control = form.get(conflict.field);
    if (!control) return;

    onConflict?.();

    control.setErrors({ serverDuplicate: conflict.message });
    control.markAsTouched();

    control.valueChanges
      .pipe(take(1), takeUntilDestroyed(destroyRef))
      .subscribe(() => {
        const { serverDuplicate: _, ...rest } = control.errors ?? {};
        control.setErrors(Object.keys(rest).length ? rest : null);
      });
  });
}
